import clsx from 'clsx';
import { useCallback, useMemo } from 'react';
import {
  AttachmentPreviewList,
  ChatAutoComplete,
  SendButton,
  useChannelStateContext,
  useComponentContext,
  useMessageInputContext,
} from 'stream-chat-react';
import { useDropzone } from 'react-dropzone';

import { GiphyBadge } from './GiphyBadge';
import { MessageInputControlButton } from './MessageInputControls';
import { EmojiPicker } from './EmojiPicker';

import { useGiphyInMessageContext } from '@/contexts/GiphyInMessageFlagContext';
import { useMessageInputCompositionControls } from './hooks/useMessageInputCompositionControls';

import type { StreamChatType } from '../../types';

export const TeamMessageInput = () => {
  const { TypingIndicator } = useComponentContext();

  const { acceptedFiles = [], multipleUploads } =
    useChannelStateContext<StreamChatType>();
  const {
    handleSubmit,
    numberOfUploads,
    text,
    uploadNewFiles,
    maxFilesLeft,
    isUploadEnabled,
  } = useMessageInputContext<StreamChatType>();
  const { isComposingGiphyMessage } = useGiphyInMessageContext();
  const {
    formatting,
    handleBoldButtonClick,
    handleCodeButtonClick,
    handleItalicsButtonClick,
    handleStrikeThroughButtonClick,
    onChange,
    placeholder,
  } = useMessageInputCompositionControls();

  const accept = useMemo(
    () =>
      acceptedFiles.reduce<Record<string, Array<string>>>(
        (mediaTypeMap, mediaType) => {
          mediaTypeMap[mediaType] ??= [];
          return mediaTypeMap;
        },
        {}
      ),
    [acceptedFiles]
  );

  const { getRootProps, getInputProps, isDragActive, isDragReject, open } =
    useDropzone({
      accept,
      disabled: !isUploadEnabled || maxFilesLeft === 0,
      multiple: multipleUploads,
      noClick: true,
      onDrop: uploadNewFiles,
    });

  const isAttachmentDisabled = !isUploadEnabled || maxFilesLeft === 0;
  const handleAttachmentButtonClick = useCallback(() => {
    if (isAttachmentDisabled) return;
    open();
  }, [isAttachmentDisabled, open]);

  return (
    <div {...getRootProps({ className: clsx(`team-message-input__wrapper`) })}>
      <input {...getInputProps({ style: { display: 'none' } })} />
      {isDragActive && (
        <div
          className={clsx('str-chat__dropzone-container', {
            'str-chat__dropzone-container--not-accepted': isDragReject,
          })}
        >
          {!isDragReject && <p>Drag your files here</p>}
          {isDragReject && <p>Some of the files will not be accepted</p>}
        </div>
      )}
      <div className="team-message-input__input">
        <div className="team-message-input__top">
          {!!numberOfUploads && <AttachmentPreviewList />}
          <div className="team-message-input__form">
            {isComposingGiphyMessage() && !numberOfUploads && <GiphyBadge />}
            <ChatAutoComplete onChange={onChange} placeholder={placeholder} />

            <SendButton
              disabled={!numberOfUploads && !text.length}
              sendMessage={handleSubmit}
            />
          </div>
        </div>
        <div className="team-message-input__bottom">
          <button
            className="message-input__controls-button attachment"
            onClick={handleAttachmentButtonClick}
            type="button"
            disabled={isAttachmentDisabled}
            aria-label="Attach files"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M5 5h14a1 1 0 011 1v10a1 1 0 01-1 1H5a1 1 0 01-1-1V6a1 1 0 011-1z"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M6.5 15.5l3.3-4 2.2 3 1.8-2.3 3.7 3.3"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M12 8.75a 1.25 1.25 0 11-2.5 0 1.25 1.25 0 012.5 0z"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <EmojiPicker />
          <MessageInputControlButton
            type="bold"
            active={formatting === 'bold'}
            onClick={handleBoldButtonClick}
          />
          <MessageInputControlButton
            type="italics"
            active={formatting === 'italics'}
            onClick={handleItalicsButtonClick}
          />
          <MessageInputControlButton
            type="strike-through"
            active={formatting === 'strike-through'}
            onClick={handleStrikeThroughButtonClick}
          />
          <MessageInputControlButton
            type="code"
            active={formatting === 'code'}
            onClick={handleCodeButtonClick}
          />
        </div>
      </div>
      {TypingIndicator && <TypingIndicator />}
    </div>
  );
};
